/**
 * Appends `?resize=w{width}` to `/assets/public` URLs so the asset optimizer
 * preserves full resolution while still serving optimised WebP.
 *
 * Without this param the optimizer caps images at 1920×1200, which crushes
 * tall sprites and under-serves Retina displays for wide hero images.
 *
 * No-ops when:
 * - the URL is not an `/assets/public` path (user photos go through `addRendition` instead)
 * - a `resize` param is already present
 * - the URL is not parseable
 */

const ASSETS_PUBLIC_PATH = '/assets/public';

/**
 * Appends `resize=w{width}` to an `/assets/public` URL.
 *
 * @param url   — absolute asset URL
 * @param width — the asset's natural width in pixels (determines the resize target)
 * @returns the URL with the resize param, or the original URL unchanged
 */
export const withAssetResize = (url: string, width: number): string => {
  try {
    const parsed = new URL(url);
    if (!parsed.pathname.startsWith(ASSETS_PUBLIC_PATH)) return url;
    if (parsed.searchParams.has('resize')) return url;
    parsed.searchParams.set('resize', `w${width}`);
    return parsed.toString();
  } catch {
    return url;
  }
};
